import React, { useMemo } from 'react';
import styles from './CompactTable.module.css';

import tables from '@/data/docs/kdf_tables.json';

type TableVariant = 'default' | 'compact' | 'minimal';

interface TableDataItem {
  parameter: string;
  type: string;
  required?: boolean | string;
  default?: string;
  description: string;
  [key: string]: any; // Allow additional properties
}

interface CompactTableProps {
  data?: TableDataItem[];
  source?: string; // Format: "filename.tableName" e.g., "lightning.ConfirmationTargets"
  columns?: string[];
  className?: string;
  variant?: TableVariant;
}

const CompactTable: React.FC<CompactTableProps> = ({ 
  data, 
  source,
  columns = ['Parameter', 'Type', 'Description'],
  className = '',
  variant = 'default'
}) => {
  // Function to sort parameters: required first (alphabetically), then optional (alphabetically)
  const sortTableData = (tableData: TableDataItem[]): TableDataItem[] => {
    return [...tableData].sort((a, b) => {
      // Determine if items are required
      const aRequired = a.required === true || a.required === 'true' || a.required === '✓';
      const bRequired = b.required === true || b.required === 'true' || b.required === '✓';
      
      // If one is required and other is not, required comes first
      if (aRequired && !bRequired) return -1;
      if (!aRequired && bRequired) return 1;
      
      // If both have same required status, sort alphabetically by parameter name
      return a.parameter.localeCompare(b.parameter);
    });
  };

  // Get table data from either inline data prop or JSON source
  const tableData = useMemo(() => {
    let rawData: TableDataItem[] = [];
    let tableVariant = variant;
    
    if (data) {
      rawData = data;
    } else if (source) {
      // Parse source like "wallet.BalanceInfo" or "orders.OrderType"
      const [fileName, tableName] = source.split('.');
      const fileData = tables[fileName as keyof typeof tables];

      if (!fileData) {
        throw new Error(`Source file "${fileName}" not found. Available files: ${Object.keys(tables).join(', ')}`);
      }
      
      if (!(tableName in fileData)) {
        throw new Error(`Table "${tableName}" not found in file "${fileName}". Available tables: ${Object.keys(fileData).join(', ')}`);
      }
      
      const tableConfig = (fileData as any)[tableName];
      rawData = tableConfig?.data || [];
      tableVariant = tableConfig?.variant || variant;
    } else {
      console.error('CompactTable: Either data or source prop is required');
      return { data: [], variant };
    }
    
    // Sort the data: required parameters first (alphabetically), then optional (alphabetically)
    const sortedData = sortTableData(rawData);
    
    return {
      data: sortedData,
      variant: tableVariant
    };
  }, [data, source, variant]);
  // Function to parse markdown links and backticks and convert to JSX
  const parseMarkdownContent = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let keyCounter = 0;

    // Combined regex to match both links [text](url) and backticks `code`
    const combinedRegex = /(\[([^\]]+)\]\(([^\)]+)\))|(`([^`]+)`)/g;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      if (match[1]) {
        // This is a link [text](url)
        parts.push(
          <a 
            key={`link-${keyCounter++}`} 
            href={match[3]} 
            className={styles.descriptionLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {match[2]}
          </a>
        );
      } else if (match[4]) {
        // This is inline code `text`
        parts.push(
          <code key={`code-${keyCounter++}`}>
            {match[5]}
          </code>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };
  const renderCell = (item: TableDataItem, column: string): React.ReactNode => {
    if (column.toLowerCase() === 'parameter') {
      const isRequired = item.required === true || item.required === '✓' || item.required === 'true';
      
      return (
        <span className={`${styles.parameterName} ${isRequired ? styles.required : styles.optional}`}>
          {item.parameter}
          {isRequired && (
            <span className={styles.requiredIndicator} aria-label="required" title="Required parameter">
              *
            </span>
          )}
        </span>
      );
    }
    
    if (column.toLowerCase() === 'type') {
      const hasDefault = item.default && item.default !== '-' && item.default !== '';
      
      return (
        <div className={styles.typeContainer}>
          <code className={styles.typeIndicator}>{item.type}</code>
          {hasDefault && (
            <div className={styles.defaultValue}>
              default: <code>{item.default}</code>
            </div>
          )}
        </div>
      );
    }
    
    if (column.toLowerCase() === 'description') {
      let description = item.description;
      
      // Handle legacy format where required/default info is in separate columns
      if (item.required === false || item.required === '✗' || item.required === 'false') {
        // Don't add default info here since it's now shown in the type column
        const prefix = 'Optional. ';
        description = description.startsWith('Optional') ? description : prefix + description;
      }
      
      return (
        <span className={styles.descriptionText}>
          {parseMarkdownContent(description)}
        </span>
      );
    }
    
    return item[column.toLowerCase()];
  };

  const effectiveVariant = tableData?.variant || variant;

  return (
    <div className={`${styles.compactTableWrapper} ${styles[effectiveVariant]} ${className}`}>
      <table className={styles.compactTable} role="table">
        <thead>
          <tr>
            {columns.map((column, index) => {
              const columnLower = column.toLowerCase();
              let headerClass = '';
              if (columnLower === 'type') {
                headerClass = styles.typeHeader;
              } else if (columnLower === 'description') {
                headerClass = styles.descriptionHeader;
              }
              
              return (
              <th 
                key={index} 
                scope="col"
                className={headerClass}
              >
                {column}
                {column.toLowerCase() === 'parameter' && (
                  <span className={styles.requiredLegend} title="* indicates required parameter">
                    <span className={styles.requiredAsterisk}>*</span> = required
                  </span>
                )}
              </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tableData?.data?.map((item: any, index: number) => (
            <tr key={item.parameter || `row-${index}`}>
              {columns.map((column, colIndex) => (
                <td key={`${item.parameter || index}-${column.toLowerCase()}`}>
                  {renderCell(item, column)}
                </td>
              ))}
            </tr>
          )) || (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', color: '#6b7280' }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CompactTable; 